import { useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient, getQueryFn } from "@/lib/queryClient";
import type { UserPreferences } from "@shared/schema";
import { useAuth } from "@/hooks/useAuth";
import { userLsKey } from "@/lib/userScopedStorage";

export interface NavigationItem {
  id: string;
  name: string;
  href: string;
  icon: any;
  current: boolean;
  isCollapsible?: boolean;
  children?: React.ReactNode;
  showTourLauncher?: boolean;
  activeChild?: string | null;
  parentId?: string | null;
  depth?: number;
  isStaticChild?: boolean;
}

export interface NavigationOrderEntry {
  id: string;
  parentId: string | null;
}

// Helper to check if making itemId a child of newParentId would create a cycle
export function wouldCreateCycle(
  itemId: string, 
  newParentId: string | null, 
  items: NavigationItem[]
): boolean {
  if (newParentId === null) return false;
  if (itemId === newParentId) return true;
  
  // Check if newParentId is a descendant of itemId
  let current = newParentId;
  const visited = new Set<string>();
  
  while (current) {
    if (visited.has(current)) return true; // Existing cycle
    visited.add(current);
    if (current === itemId) return true; // Would create cycle
    const parent = items.find(i => i.id === current)?.parentId;
    current = parent || '';
  }
  
  return false;
}

// Helper to flatten a tree structure to ordered list with depth
export function flattenNavigation(
  items: NavigationItem[],
  orderEntries: NavigationOrderEntry[]
): NavigationItem[] {
  const itemMap = new Map(items.map(item => [item.id, item]));
  const result: NavigationItem[] = [];
  const addedIds = new Set<string>();
  
  // Build a combined parentId map from both orderEntries AND default items
  // Default parentId takes precedence for items that have an explicit default parent
  // Saved preferences only override items that don't have a default parent OR user explicitly moved them
  const combinedParentMap = new Map<string, string | null>();
  const defaultParentMap = new Map<string, string | null>();
  
  // First, set default parentId from items
  for (const item of items) {
    const defaultParent = item.parentId || null;
    defaultParentMap.set(item.id, defaultParent);
    combinedParentMap.set(item.id, defaultParent);
  }
  
  // Then, override with orderEntries (saved preferences)
  // But only if the item doesn't have a default parent, OR the saved parent is non-null (user explicitly moved it)
  for (const entry of orderEntries) {
    if (itemMap.has(entry.id)) {
      const defaultParent = defaultParentMap.get(entry.id);
      // If item has a default parent, only override if user explicitly set a different parent
      if (defaultParent && entry.parentId === null) {
        // Default parent exists but saved says null - keep the default parent
        continue;
      }
      // Migration: "Settings" was moved from under "Review" to a top-level menu item.
      // Ignore stale saved orders that still nest it under "review".
      if (entry.id === "settings" && entry.parentId === "review") {
        continue;
      }
      // "Master Admin" must always be a top-level menu item.
      // Ignore any saved order that nests it under a section.
      if (entry.id === "master-admin" && entry.parentId !== null) {
        continue;
      }
      combinedParentMap.set(entry.id, entry.parentId);
    }
  }
  
  // Detect and break cycles
  Array.from(combinedParentMap.entries()).forEach(([id, parentId]) => {
    if (parentId && wouldCreateCycle(id, parentId, Array.from(itemMap.values()).map(item => ({
      ...item,
      parentId: combinedParentMap.get(item.id) || null
    })))) {
      combinedParentMap.set(id, null); // Break cycle by resetting to root
    }
  });
  
  // Build children map from combined parent map
  const childrenMap = new Map<string | null, string[]>();
  Array.from(combinedParentMap.entries()).forEach(([id, parentId]) => {
    const parentKey = parentId || null;
    if (!childrenMap.has(parentKey)) {
      childrenMap.set(parentKey, []);
    }
    childrenMap.get(parentKey)!.push(id);
  });
  
  // Determine order of items: use orderEntries order for saved items, default order for new items
  const savedOrder = orderEntries.map(e => e.id).filter(id => itemMap.has(id));
  const savedOrderSet = new Set(savedOrder);
  const defaultOrder = items.map(item => item.id);
  
  // Create a combined order: saved items in their saved order, with new items
  // inserted at their default position (right after the nearest preceding
  // default sibling that exists in the saved order) instead of at the end.
  const allIds = [...savedOrder];
  for (let idx = 0; idx < defaultOrder.length; idx++) {
    const id = defaultOrder[idx];
    if (savedOrderSet.has(id)) continue;
    let insertAt = allIds.length;
    for (let i = idx - 1; i >= 0; i--) {
      const pos = allIds.indexOf(defaultOrder[i]);
      if (pos !== -1) {
        insertAt = pos + 1;
        break;
      }
    }
    allIds.splice(insertAt, 0, id);
  }
  
  // Recursive function to add items with depth
  function addItemsRecursively(parentId: string | null, depth: number) {
    const childIds = childrenMap.get(parentId) || [];
    
    // Sort children: static children first (in their default order), then dynamic children
    const sortedChildIds = childIds.sort((a, b) => {
      const itemA = itemMap.get(a);
      const itemB = itemMap.get(b);
      
      // Static children always come first
      if (itemA?.isStaticChild && !itemB?.isStaticChild) return -1;
      if (!itemA?.isStaticChild && itemB?.isStaticChild) return 1;
      
      // Within same type, sort by position in allIds
      const aIdx = allIds.indexOf(a);
      const bIdx = allIds.indexOf(b);
      return aIdx - bIdx;
    });
    
    for (const id of sortedChildIds) {
      if (addedIds.has(id)) continue; // Prevent duplicates
      const item = itemMap.get(id);
      if (item) {
        result.push({ ...item, parentId, depth });
        addedIds.add(id);
        addItemsRecursively(id, depth + 1);
      }
    }
  }
  
  addItemsRecursively(null, 0);
  
  // Add any remaining items not yet added (shouldn't happen, but safety net)
  for (const item of items) {
    if (!addedIds.has(item.id)) {
      const parentId = combinedParentMap.get(item.id) || null;
      const depth = parentId ? 1 : 0;
      result.push({ ...item, parentId, depth });
      addedIds.add(item.id);
    }
  }
  
  return result;
}

// Helper to convert flat list back to order entries
export function toOrderEntries(items: NavigationItem[]): NavigationOrderEntry[] {
  return items.map(item => ({
    id: item.id,
    parentId: item.parentId || null
  }));
}

// Helper to recalculate depths for all descendants of a moved item
export function recalculateDepths(items: NavigationItem[]): NavigationItem[] {
  const itemMap = new Map(items.map(item => [item.id, item]));
  
  function getDepth(id: string, visited: Set<string> = new Set()): number {
    if (visited.has(id)) return 0; // Cycle detected
    visited.add(id);
    
    const item = itemMap.get(id);
    if (!item || !item.parentId) return 0;
    return 1 + getDepth(item.parentId, visited);
  }
  
  return items.map(item => ({
    ...item,
    depth: getDepth(item.id)
  }));
}

// Get a node and ALL its descendants in correct order (as they appear in the flat array)
export function getSubtree(items: NavigationItem[], nodeId: string): NavigationItem[] {
  const result: NavigationItem[] = [];
  const node = items.find(item => item.id === nodeId);
  if (!node) return result;
  
  result.push(node);
  
  // Recursively add all descendants
  function addDescendants(parentId: string) {
    for (const item of items) {
      if (item.parentId === parentId) {
        result.push(item);
        addDescendants(item.id);
      }
    }
  }
  
  addDescendants(nodeId);
  return result;
}

// Remove a node and all its descendants from the items array
export function removeSubtree(items: NavigationItem[], nodeId: string): NavigationItem[] {
  const subtreeIds = new Set(getSubtree(items, nodeId).map(item => item.id));
  return items.filter(item => !subtreeIds.has(item.id));
}

// Insert nodes at a specified position relative to target
export function insertAtPosition(
  items: NavigationItem[],
  nodesToInsert: NavigationItem[],
  targetId: string,
  position: 'before' | 'after' | 'inside'
): NavigationItem[] {
  if (nodesToInsert.length === 0) return items;
  
  const targetIndex = items.findIndex(item => item.id === targetId);
  if (targetIndex === -1) {
    // Target not found, append at end
    return [...items, ...nodesToInsert];
  }
  
  const target = items[targetIndex];
  
  if (position === 'inside') {
    // Insert as last child of target (after target and all its current descendants)
    const targetSubtree = getSubtree(items, targetId);
    const lastDescendantIndex = items.findIndex(item => item.id === targetSubtree[targetSubtree.length - 1].id);
    const insertIndex = lastDescendantIndex + 1;
    return [
      ...items.slice(0, insertIndex),
      ...nodesToInsert,
      ...items.slice(insertIndex)
    ];
  } else if (position === 'before') {
    return [
      ...items.slice(0, targetIndex),
      ...nodesToInsert,
      ...items.slice(targetIndex)
    ];
  } else { // 'after'
    // Insert after target AND all its descendants
    const targetSubtree = getSubtree(items, targetId);
    const lastDescendantIndex = items.findIndex(item => item.id === targetSubtree[targetSubtree.length - 1].id);
    const insertIndex = lastDescendantIndex + 1;
    return [
      ...items.slice(0, insertIndex),
      ...nodesToInsert,
      ...items.slice(insertIndex)
    ];
  }
}

// Complete move operation that handles subtree extraction, parent changes, and positioning
export function moveNode(
  items: NavigationItem[],
  nodeId: string,
  targetId: string,
  position: 'before' | 'after' | 'inside',
  saveCallback: (items: NavigationItem[]) => void
): NavigationItem[] {
  // Don't move if source equals target
  if (nodeId === targetId) return items;
  
  const node = items.find(item => item.id === nodeId);
  const target = items.find(item => item.id === targetId);
  
  if (!node || !target) return items;
  
  // Prevent moving if target is a descendant of node (would create cycle)
  const nodeSubtree = getSubtree(items, nodeId);
  if (nodeSubtree.some(item => item.id === targetId)) {
    console.warn('Cannot move: target is a descendant of the node being moved');
    return items;
  }
  
  // Determine the new parentId based on position
  let newParentId: string | null;
  if (position === 'inside') {
    // Nesting inside target - target becomes the parent
    newParentId = targetId;
  } else {
    // Placing before/after target - adopt target's parent
    newParentId = target.parentId || null;
  }
  
  // Extract the subtree (node + all descendants)
  const subtree = getSubtree(items, nodeId);
  const remainingItems = removeSubtree(items, nodeId);
  
  // Update the moved node's parentId (descendants keep their relative parentIds)
  const updatedSubtree = subtree.map((item, index) => {
    if (index === 0) {
      // This is the root of the subtree - update its parent
      return { ...item, parentId: newParentId };
    }
    return item;
  });
  
  // Insert at the correct position
  let result = insertAtPosition(remainingItems, updatedSubtree, targetId, position);
  
  // Recalculate depths for all items
  result = recalculateDepths(result);
  
  // Sort children within each parent: static children first, then by position
  result = sortChildrenWithinParents(result);
  
  // Call the save callback
  saveCallback(result);
  
  return result;
}

// Helper to ensure static children come first within each parent
function sortChildrenWithinParents(items: NavigationItem[]): NavigationItem[] {
  // Group items by parentId
  const parentMap = new Map<string | null, NavigationItem[]>();
  const rootItems: NavigationItem[] = [];
  
  for (const item of items) {
    const parentId = item.parentId || null;
    if (!parentMap.has(parentId)) {
      parentMap.set(parentId, []);
    }
    parentMap.get(parentId)!.push(item);
  }
  
  // Rebuild the array in tree order with static children first
  const result: NavigationItem[] = [];
  const addedIds = new Set<string>();
  
  function addItemsRecursively(parentId: string | null) {
    const children = parentMap.get(parentId) || [];
    
    // Sort: static children first, then dynamic children (preserving their relative order)
    const staticChildren = children.filter(c => c.isStaticChild);
    const dynamicChildren = children.filter(c => !c.isStaticChild);
    const sortedChildren = [...staticChildren, ...dynamicChildren];
    
    for (const child of sortedChildren) {
      if (!addedIds.has(child.id)) {
        result.push(child);
        addedIds.add(child.id);
        addItemsRecursively(child.id);
      }
    }
  }
  
  addItemsRecursively(null);
  
  // Add any orphaned items
  for (const item of items) {
    if (!addedIds.has(item.id)) {
      result.push(item);
    }
  }
  
  return result;
}

const STORAGE_KEY = "goalvia_navigation_order";

export function useNavigationOrder(defaultItems: NavigationItem[], enabled: boolean = true) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const userIdRef = useRef<string | null>(userId);
  const [items, setItems] = useState<NavigationItem[]>(defaultItems);
  const previousDefaultItemsRef = useRef<NavigationItem[]>(defaultItems);
  const initializedRef = useRef(false);

  // Keep the ref in sync so callbacks always see the current userId.
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Fetch preferences from backend - use returnNull to prevent crashes on 401
  // Gate query with enabled flag to prevent firing when not authenticated
  const { data: preferences, isLoading } = useQuery<UserPreferences | null>({
    queryKey: ["/api/user/preferences"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled,
  });

  // Mutation to save preferences to backend
  // Destructure stable mutate function to avoid stale closures
  const { mutate: saveNavigationOrder } = useMutation({
    mutationFn: async (navigationOrder: NavigationOrderEntry[]) => {
      return await apiRequest("PATCH", "/api/user/preferences", { navigationOrder });
    },
    // Don't invalidate here - it causes infinite loop since preferences is in useEffect deps
  });

  // Helper to parse stored order (supports both old string[] and new NavigationOrderEntry[] format)
  const parseStoredOrder = (stored: unknown): NavigationOrderEntry[] => {
    if (!Array.isArray(stored)) return [];
    if (stored.length === 0) return [];
    
    // Check if it's the old format (array of strings)
    if (typeof stored[0] === 'string') {
      return (stored as string[]).map(id => ({ id, parentId: null }));
    }
    
    // New format (array of objects with id and parentId)
    return stored as NavigationOrderEntry[];
  };

  // Initialize items from backend preferences or localStorage (only once)
  useEffect(() => {
    if (isLoading || initializedRef.current) return;

    initializedRef.current = true;

    try {
      let orderEntries: NavigationOrderEntry[] = [];

      // Try to get from backend first
      if (preferences?.navigationOrder && Array.isArray(preferences.navigationOrder)) {
        orderEntries = parseStoredOrder(preferences.navigationOrder);
      } else {
        // Fallback to localStorage
        const stored = localStorage.getItem(userLsKey(STORAGE_KEY, userIdRef.current));
        if (stored) {
          orderEntries = parseStoredOrder(JSON.parse(stored));
        }
      }

      if (orderEntries.length > 0) {
        const flattenedItems = flattenNavigation(defaultItems, orderEntries);
        setItems(flattenedItems);
      }
    } catch (error) {
      console.error("Failed to load navigation order:", error);
    }
  }, [preferences, isLoading]);

  // Update items when defaultItems change (e.g., current page changes)
  useEffect(() => {
    const defaultMap = new Map(defaultItems.map(item => [item.id, item]));
    const prevMap = new Map(previousDefaultItemsRef.current.map(item => [item.id, { current: item.current, activeChild: item.activeChild }]));
    
    const hasCurrentChanged = defaultItems.some(item => {
      const prev = prevMap.get(item.id);
      if (!prev) return false;
      return prev.current !== item.current;
    });

    // Check if activeChild changed for any collapsible item (e.g., Files switching between Media/Drive)
    const hasActiveChildChanged = defaultItems.some(item => {
      const prev = prevMap.get(item.id);
      if (!prev) return false;
      return prev.activeChild !== item.activeChild;
    });

    const newDefaultIds = defaultItems.map(item => item.id);
    const prevDefaultIds = previousDefaultItemsRef.current.map(item => item.id);
    const hasNewItems = newDefaultIds.some(id => !prevDefaultIds.includes(id));

    if (hasCurrentChanged || hasActiveChildChanged || hasNewItems) {
      setItems((currentItems) => {
        const currentItemIds = new Set(currentItems.map(item => item.id));
        const defaultIds = new Set(defaultItems.map(item => item.id));
        
        // Filter out items that are no longer in defaultItems (e.g., "suggestions" for non-admins)
        const updatedItems: NavigationItem[] = [];
        for (const prevItem of currentItems) {
          if (!defaultIds.has(prevItem.id)) continue;
          const defaultItem = defaultMap.get(prevItem.id);
          if (defaultItem) {
            // Copy all dynamic properties from defaultItem (current, children, activeChild, etc.)
            updatedItems.push({ 
              ...prevItem, 
              current: defaultItem.current,
              children: defaultItem.children,
              isCollapsible: defaultItem.isCollapsible,
              showTourLauncher: defaultItem.showTourLauncher,
              activeChild: defaultItem.activeChild
            });
          }
        }
        
        // Insert new items at their default position (right after the nearest
        // preceding default sibling already present) instead of at the end.
        const merged = [...updatedItems];
        for (let idx = 0; idx < defaultItems.length; idx++) {
          const item = defaultItems[idx];
          if (currentItemIds.has(item.id)) continue;
          let insertAt = merged.length;
          for (let i = idx - 1; i >= 0; i--) {
            const pos = merged.findIndex(m => m.id === defaultItems[i].id);
            if (pos !== -1) {
              insertAt = pos + 1;
              break;
            }
          }
          merged.splice(insertAt, 0, item);
        }
        
        return merged;
      });
      previousDefaultItemsRef.current = defaultItems;
    }
  }, [defaultItems]);

  const reorderItems = useCallback((newOrder: NavigationItem[]) => {
    setItems(newOrder);
    try {
      const orderEntries = toOrderEntries(newOrder);
      
      // Save to backend
      saveNavigationOrder(orderEntries);
      
      // Also save to localStorage as backup
      localStorage.setItem(userLsKey(STORAGE_KEY, userIdRef.current), JSON.stringify(orderEntries));
    } catch (error) {
      console.error("Failed to save navigation order:", error);
    }
  }, [saveNavigationOrder]);

  const setItemParent = useCallback((itemId: string, newParentId: string | null) => {
    setItems(currentItems => {
      // Prevent cycles
      if (wouldCreateCycle(itemId, newParentId, currentItems)) {
        console.warn('Cannot nest item: would create circular reference');
        return currentItems;
      }
      
      // Find the item to move
      const itemToMove = currentItems.find(item => item.id === itemId);
      if (!itemToMove) return currentItems;
      
      // Simply update the parentId of the moved item
      const updatedItems = currentItems.map(item => 
        item.id === itemId ? { ...item, parentId: newParentId } : item
      );
      
      // Re-flatten the navigation to properly group children with their parents
      // This ensures static children come first and dynamic children come after
      const tempOrderEntries = toOrderEntries(updatedItems);
      const finalItems = flattenNavigation(updatedItems, tempOrderEntries);
      
      const orderEntries = toOrderEntries(finalItems);
      saveNavigationOrder(orderEntries);
      localStorage.setItem(userLsKey(STORAGE_KEY, userIdRef.current), JSON.stringify(orderEntries));
      
      return finalItems;
    });
  }, [saveNavigationOrder]);

  const resetOrder = useCallback(() => {
    const resetItems = defaultItems.map(item => ({ ...item, parentId: null, depth: 0 }));
    setItems(resetItems);
    try {
      // Clear backend preference
      saveNavigationOrder([]);
      
      // Clear localStorage
      localStorage.removeItem(userLsKey(STORAGE_KEY, userIdRef.current));
    } catch (error) {
      console.error("Failed to reset navigation order:", error);
    }
  }, [defaultItems, saveNavigationOrder]);

  const moveNodeWithSave = useCallback((
    nodeId: string,
    targetId: string,
    position: 'before' | 'after' | 'inside'
  ) => {
    setItems(currentItems => {
      const result = moveNode(currentItems, nodeId, targetId, position, (newItems) => {
        const orderEntries = toOrderEntries(newItems);
        saveNavigationOrder(orderEntries);
        localStorage.setItem(userLsKey(STORAGE_KEY, userIdRef.current), JSON.stringify(orderEntries));
      });
      return result;
    });
  }, [saveNavigationOrder]);

  return { items, reorderItems, setItemParent, resetOrder, moveNode: moveNodeWithSave };
}
